import { Injectable, inject, NgZone } from '@angular/core';
import { Observable, BehaviorSubject, firstValueFrom, map, from, of, catchError } from 'rxjs';
import { Child, CreateChildDto, UpdateChildDto } from '../interfaces/child.interface';
import { ChildAuthService } from './child-auth.service';
import { v4 as uuidv4 } from 'uuid';
import { FirestoreUtilsService } from './firestore-utils.service';

// Firebase imports
import { 
  Firestore, 
  collection, 
  doc,
  addDoc, 
  setDoc,
  getDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  getDocs,
  collectionData
} from '@angular/fire/firestore';
import { Auth, onAuthStateChanged, User } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})
export class ChildrenService {
  private childrenSubject = new BehaviorSubject<Child[]>([]);
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private childAuth = inject(ChildAuthService);
  private zone = inject(NgZone);
  private firestoreUtils = inject(FirestoreUtilsService);
  
  // Store the unsubscribe function to clean up on service destruction
  private unsubscribeAuth: (() => void) | null = null;

  constructor() {
    console.log('ChildrenService constructor called');
    this.initializeChildren();
    
    // Initialize unreadChores field for existing children
    this.initializeUnreadChoresField();
  }

  private initializeChildren() {
    console.log('Initializing children...');
    
    // Clean up any existing subscription
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
      this.unsubscribeAuth = null;
    }
    
    // Set up auth state listener
    this.unsubscribeAuth = onAuthStateChanged(this.auth, 
      (user) => {
        // Run inside Angular zone to ensure proper change detection
        this.zone.run(() => this.handleAuthStateChange(user));
      },
      (error) => {
        console.error('Auth state error:', error);
        this.zone.run(() => this.childrenSubject.next([]));
      }
    );
  }
  
  private handleAuthStateChange(user: User | null) {
    console.log('Auth state changed:', user?.uid);
    
    if (!user) {
      console.log('No user logged in');
      this.childrenSubject.next([]);
      return;
    }

    try {
      console.log('Creating query for user:', user.uid);
      
      // Create the query inside the zone
      this.fetchChildrenForUser(user.uid);
    } catch (error) {
      console.error('Error initializing children:', error);
      this.childrenSubject.next([]);
    }
  }
  
  private async fetchChildrenForUser(userId: string) {
    console.log('Fetching children for user:', userId);
    
    try {
      // Use FirestoreUtilsService to create a parent-scoped query
      const childrenQuery = await this.firestoreUtils.createParentScopedQuery('children');
      
      // Add orderBy constraint to the query
      const orderedQuery = query(childrenQuery, orderBy('createdAt', 'desc'));
      
      console.log('Created parent-scoped children query');

      // Get the collection data using collectionData
      const children$ = collectionData(orderedQuery, { idField: 'id' }).pipe(
        map(children => {
          console.log('Raw children data:', children);
          return children as Child[];
        }),
        catchError(error => {
          console.error('Error fetching children:', error);
          return of([]);
        })
      );

      // Subscribe to the query results inside the zone
      const subscription = children$.subscribe({
      next: children => {
        console.log('Children fetched successfully:', children);
        
        // Check if we have any optimistic updates to apply
        if (this.optimisticUpdates.size > 0) {
          // Apply optimistic updates to the fetched children
          const updatedChildren = children.map(child => {
            // If this child has an optimistic update, use that value instead
            if (this.optimisticUpdates.has(child.id)) {
              const optimisticPoints = this.optimisticUpdates.get(child.id)!;
              return { ...child, points: optimisticPoints };
            }
            return child;
          });
          this.childrenSubject.next(updatedChildren);
        } else {
          // No optimistic updates, just use the fetched data
          this.childrenSubject.next(children);
        }
      },
      error: error => {
        console.error('Error in children subscription:', error);
        this.childrenSubject.next([]);
      },
      complete: () => {
        console.log('Children subscription completed');
      }
    });
    
    // Return the subscription for potential cleanup
    return subscription;
    } catch (error) {
      console.error('Error setting up children subscription:', error);
      this.childrenSubject.next([]);
      return null;
    }
  }

  getChildren(): Observable<Child[]> {
    console.log('getChildren called');
    return this.childrenSubject.asObservable();
  }
  
  // Keep track of children that have been optimistically updated
  private optimisticUpdates = new Map<string, number>();

  /**
   * Updates the children array locally without triggering a Firestore update
   * This is used for optimistic UI updates to prevent card movement during points adjustments
   * @param updatedChildren Array of children with updated values
   */
  updateChildrenLocally(updatedChildren: Child[]): void {
    // Get the current children array
    const currentChildren = this.childrenSubject.getValue();
    
    // Create a map for faster lookups
    const updatedChildrenMap = new Map<string, Child>();
    updatedChildren.forEach(child => {
      updatedChildrenMap.set(child.id, child);
      
      // Track this optimistic update to prevent double-counting
      this.optimisticUpdates.set(child.id, child.points);
      
      // Set a timeout to clear this tracking after 5 seconds
      // This ensures we don't permanently block updates if something goes wrong
      setTimeout(() => {
        this.optimisticUpdates.delete(child.id);
      }, 5000);
    });
    
    // Create a new array with updated children
    const newChildren = currentChildren.map(child => {
      return updatedChildrenMap.has(child.id) ? updatedChildrenMap.get(child.id)! : child;
    });
    
    // Update the BehaviorSubject
    this.childrenSubject.next(newChildren);
  }

  getChild(childId: string): Observable<Child | null> {
    return this.childrenSubject.pipe(
      map(children => children.find(child => child.id === childId) || null)
    );
  }

  async createChild(data: CreateChildDto): Promise<Child> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    try {
      const childId = uuidv4();
      const now = new Date();
      const child: Child = {
        id: childId,
        parentId: user.uid,
        displayName: data.displayName,
        profilePicture: data.profilePicture || '',
        points: data.points || 0,
        createdAt: now,
        updatedAt: now
      };

      // Use direct Firestore operations
      const childRef = doc(this.firestore, 'children', childId);
      await setDoc(childRef, child);
      return child;
    } catch (error) {
      console.error('Error creating child:', error);
      throw error;
    }
  }

  async updateChild(childId: string, data: UpdateChildDto): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    try {
      const child = await firstValueFrom(this.getChild(childId));
      if (!child) throw new Error('Child not found');
      if (child.parentId !== user.uid) throw new Error('Not authorized');

      // Use direct Firestore operations
      const childRef = doc(this.firestore, 'children', childId);
      await updateDoc(childRef, {
        ...data,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating child:', error);
      throw error;
    }
  }

  async deleteChild(childId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    try {
      const child = await firstValueFrom(this.getChild(childId));
      if (!child) throw new Error('Child not found');
      if (child.parentId !== user.uid) throw new Error('Not authorized');

      // Use direct Firestore operations
      const childRef = doc(this.firestore, 'children', childId);
      await deleteDoc(childRef);
    } catch (error) {
      console.error('Error deleting child:', error);
      throw error;
    }
  }

  async adjustPoints(childId: string, amount: number, reason: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    try {
      await this.updateChildPoints(childId, amount, reason);
    } catch (error) {
      console.error('Error adjusting points:', error);
      throw error;
    }
  }

  private async updateChildPoints(childId: string, amount: number, reason: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    try {
      const child = await firstValueFrom(this.getChild(childId));
      if (!child) throw new Error('Child not found');
      if (child.parentId !== user.uid) throw new Error('Not authorized');

      const newPoints = child.points + amount;
      const now = new Date();
      
      // Use direct Firestore operations
      const childRef = doc(this.firestore, 'children', childId);
      await updateDoc(childRef, {
        points: newPoints,
        updatedAt: now
      });

      const transactionId = uuidv4();
      const transactionRef = doc(this.firestore, 'pointsTransactions', transactionId);
      await setDoc(transactionRef, {
        childId,
        parentId: user.uid,
        amount,
        reason,
        timestamp: now
      });

      // Update the child in ChildAuthService if this is the currently logged in child
      const currentChild = this.childAuth.getLoggedInChild();
      if (currentChild && currentChild.id === childId) {
        this.childAuth.updateChildState({ ...currentChild, points: newPoints });
      }
    } catch (error) {
      console.error('Error updating child points:', error);
      throw error;
    }
  }
  
  /**
   * Initializes the unreadChores field for all existing child records
   * This is a one-time migration to ensure all children have the field
   */
  private async initializeUnreadChoresField(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      console.log('No user logged in, skipping unreadChores initialization');
      return;
    }
    
    try {
      console.log('Initializing unreadChores field for all children...');
      
      // Get all children for the current parent
      const childrenCollection = collection(this.firestore, 'children');
      const childrenQuery = query(
        childrenCollection,
        where('parentId', '==', user.uid)
      );
      
      const snapshot = await getDocs(childrenQuery);
      const updatePromises: Promise<void>[] = [];
      
      snapshot.forEach(doc => {
        const childData = doc.data();
        
        // Only update if unreadChores field doesn't exist
        if (childData['unreadChores'] === undefined) {
          console.log(`Initializing unreadChores for child ${doc.id}`);
          const childRef = doc.ref;
          updatePromises.push(
            updateDoc(childRef, {
              unreadChores: false,
              lastChoreViewTime: new Date()
            })
          );
        }
      });
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        console.log(`Initialized unreadChores for ${updatePromises.length} children`);
      } else {
        console.log('All children already have unreadChores field');
      }
    } catch (error) {
      console.error('Error initializing unreadChores field:', error);
    }
  }
}